import React, { useState, useEffect, useRef, useCallback } from 'react';
import { StickyNote, Check, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Inline auto-saving notes editor for a customer.
 *
 * Used in two places (so the same notes appear/edit identically in both):
 *   1. CustomersEnhanced.jsx — under the "Notes and Activity" row on the
 *      customer's Finance/profile screen.
 *   2. JobDetailPanel.jsx — under the Photos tile when a worker opens a job.
 *
 * Behaviour:
 *   - Debounced auto-save (800ms) via PATCH /api/customers/{id} with {notes}
 *   - Shows "Saved" tick or "Saving…" spinner so the user knows the value
 *     persisted (workers on site need that reassurance)
 *   - Calls `onSaved(newNotes)` so the parent can update its local customer
 *     state in lockstep — keeps both editor instances in sync within one
 *     session.
 *   - Hard cap 2000 chars to keep things sane (UK BACS would yell at us
 *     long before this, but it's a free-form field).
 */
export default function CustomerNotesEditor({
  customerId,
  initialNotes,
  onSaved,
  placeholder = 'Access notes, gate codes, allergies, anything the team should know…',
  title = 'Notes',
  className = '',
}) {
  const [value, setValue] = useState(initialNotes || '');
  const [status, setStatus] = useState('idle'); // idle | saving | saved | error
  const [errorMsg, setErrorMsg] = useState('');
  const timerRef = useRef(null);
  const lastSavedRef = useRef(initialNotes || '');
  const onSavedRef = useRef(onSaved);

  // Keep onSaved ref fresh without re-triggering the save effect
  useEffect(() => {
    onSavedRef.current = onSaved;
  }, [onSaved]);

  // If no initialNotes were provided (e.g. JobDetailPanel only has a
  // customer_id, not the full customer record), fetch the customer once
  // so the textarea starts pre-populated with whatever's in the DB.
  // CRITICAL: if the user has already typed BEFORE the fetch lands,
  // don't clobber their input.
  useEffect(() => {
    if (initialNotes !== undefined && initialNotes !== null) return;
    if (!customerId) return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('streakfree_token') || localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/customers/${customerId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) return;
        const j = await res.json();
        if (cancelled) return;
        const fetched = j.notes || '';
        // Only seed local state if the user hasn't begun editing yet.
        // We detect "has typed" by checking against lastSavedRef which
        // updates only on (a) successful save or (b) this very block.
        setValue((prev) => (prev === lastSavedRef.current ? fetched : prev));
        lastSavedRef.current = fetched;
      } catch (_) { /* swallow — empty field is fine */ }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  // When the customer record reloads externally (e.g. the OTHER editor
  // instance wrote a new value), sync the local textarea — but only if
  // the user hasn't started typing something else in the meantime, AND
  // only when the parent actually supplies an initialNotes prop. If
  // initialNotes is undefined (CustomerDetailModal relies on the
  // auto-fetch path below), we MUST NOT reset, or we'll wipe the user's
  // input every parent re-render.
  useEffect(() => {
    if (initialNotes === undefined || initialNotes === null) return;
    if (initialNotes !== lastSavedRef.current) {
      setValue(initialNotes);
      lastSavedRef.current = initialNotes;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialNotes]);

  const persist = useCallback(async (newValue) => {
    if (!customerId) return;
    if (newValue === lastSavedRef.current) {
      setStatus('saved');
      return;
    }
    setStatus('saving');
    setErrorMsg('');
    try {
      const token = localStorage.getItem('streakfree_token') || localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/customers/${customerId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ notes: newValue }),
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
          const j = await res.json();
          detail = j.detail || detail;
        } catch (_) { /* swallow */ }
        throw new Error(detail);
      }
      lastSavedRef.current = newValue;
      setStatus('saved');
      if (onSavedRef.current) onSavedRef.current(newValue);
    } catch (e) {
      setStatus('error');
      setErrorMsg(e.message || 'Could not save notes');
    }
  }, [customerId]);

  const handleChange = (e) => {
    const v = e.target.value.slice(0, 2000);
    setValue(v);
    setStatus('idle');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => persist(v), 800);
  };

  const handleBlur = () => {
    // Flush any pending debounce immediately so closing the tab/panel saves
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (value !== lastSavedRef.current) {
      persist(value);
    }
  };

  return (
    <div className={`bg-white rounded-2xl border border-zinc-200 p-4 ${className}`} data-testid="customer-notes-editor">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center">
            <StickyNote size={18} className="text-amber-600" />
          </div>
          <div>
            <p className="font-semibold text-zinc-900 text-sm leading-tight">{title}</p>
            <p className="text-xs text-zinc-500 leading-tight">Synced across the app — workers see this on the job</p>
          </div>
        </div>
        <div className="text-xs flex items-center gap-1.5 min-h-[20px]" aria-live="polite">
          {status === 'saving' && (
            <>
              <Loader2 size={14} className="text-zinc-400 animate-spin" />
              <span className="text-zinc-500">Saving…</span>
            </>
          )}
          {status === 'saved' && (
            <>
              <Check size={14} className="text-emerald-500" />
              <span className="text-emerald-600">Saved</span>
            </>
          )}
          {status === 'error' && (
            <span className="text-red-600" title={errorMsg}>Couldn&apos;t save</span>
          )}
        </div>
      </div>
      <textarea
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        rows={4}
        maxLength={2000}
        placeholder={placeholder}
        className="w-full px-3 py-2 rounded-xl border border-zinc-200 focus:border-amber-400 focus:ring-2 focus:ring-amber-200 outline-none text-sm resize-y bg-amber-50/30"
        data-testid="customer-notes-textarea"
      />
      <div className="flex items-center justify-between mt-1">
        {status === 'error' ? (
          <p className="text-[11px] text-red-600">{errorMsg}</p>
        ) : (
          <p className="text-[11px] text-zinc-400">Auto-saves as you type</p>
        )}
        <p className="text-[11px] text-zinc-400 tabular-nums">{value.length}/2000</p>
      </div>
    </div>
  );
}
